import { WarningIconV2 } from 'pages/Accomodation/icons/WarningIconV2'
import React, { forwardRef } from 'react'
import styled from 'styled-components'
import { Icon } from 'styling/GlobalStyles'

// forward ref
export const Input = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & {
    label?: string
    width?: string
    error?: string
    noPaddingRight?: boolean
    inputSize?: 'small' | 'medium' | 'large'
    align?: 'left' | 'center' | 'right'
  }
>(
  (
    {
      label,
      name,
      children,
      width,
      error,
      noPaddingRight,
      inputSize = 'medium',
      align = 'left',
      ...props
    },
    ref,
  ) => {
    return (
      <InputWrapper width={width}>
        {label && <Label htmlFor={name}>{label}</Label>}
        <FieldWrapper>
          <StyledInput
            {...props}
            name={name}
            id={name}
            ref={ref}
            invalid={!!error}
            noPaddingRight={noPaddingRight}
            inputSize={inputSize}
            align={align}
          />
          {children}
        </FieldWrapper>
        {error && (
          <InvalidText>
            <Icon size='0.75rem'>
              <WarningIconV2 color='currentColor' />
            </Icon>
            {error}
          </InvalidText>
        )}
      </InputWrapper>
    )
  },
)
Input.displayName = 'Input'

export const Textarea = ({
  label,
  name,
  children,
  error,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; error?: string }) => {
  return (
    <InputWrapper>
      {label && <Label htmlFor={name}>{label}</Label>}
      <FieldWrapper>
        <StyledTextarea rows={4} {...props} name={name} invalid={!!error} />
        {children}
      </FieldWrapper>
      {error && (
        <InvalidText>
          <Icon size='0.75rem'>
            <WarningIconV2 color='currentColor' />
          </Icon>
          {error}
        </InvalidText>
      )}
    </InputWrapper>
  )
}

export const InputWrapper = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  width: ${(props) => props.width || '100%'};

  > * {
    min-width: 0;
  }
`

export const FieldWrapper = styled.div`
  position: relative;
  min-width: 0;
`

export const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
  margin-bottom: 0.25rem;
`

export const StyledInput = styled.input<{
  invalid?: boolean
  noPaddingRight?: boolean
  inputSize?: 'small' | 'medium' | 'large'
  align?: 'left' | 'center' | 'right'
}>`
  width: 100%;
  border: 1px solid ${(props) => props.theme.tableRowBorder};
  border-radius: 12px;
  padding: 0.75rem 1rem;
  color: ${(props) => props.theme.textColor};
  background: #22202080;
  outline: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: ${(props) => (props.noPaddingRight ? '0' : '2.5rem')};
  font-size: 1rem;
  text-align: ${(props) => props.align || 'left'};
  ${(props) => props.inputSize === 'small' && 'font-size: 0.875rem; padding: 0.5rem 0.75rem;'}
  ${(props) => props.inputSize === 'medium' && 'font-size: 1rem; padding: 0.75rem 1rem;'}
  ${(props) => props.inputSize === 'large' && 'font-size: 1.125rem; padding: 0.75rem 1rem;'}

  &:not(:placeholder-shown) {
    border: 1px solid ${(props) => props.theme.textFaded};
  }

  &:hover {
    border: 1px solid ${(props) => props.theme.colorDividerLight};
    transition: 0.2s ease;
  }
  &:active,
  &:focus {
    border: 1px solid ${(props) => props.theme.colorOrange};
  }

  ${(props) =>
    props.invalid &&
    `
    border: 1px solid ${props.theme.colorRed};
  `}

  /* Chrome, Safari, Edge */
&::-webkit-outer-spin-button,
&::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }

  &:read-only {
    color: ${(props) => props.theme.textFaded};
    border: 1px solid ${(props) => props.theme.tableRowBorder};
  }
`

export const StyledTextarea = styled.textarea<{ invalid?: boolean }>`
  width: 100%;
  height: 100%;
  border: 1px solid ${(props) => props.theme.tableRowBorder};
  border-radius: 12px;
  padding: 0.75rem 1rem;
  color: ${(props) => props.theme.textColor};
  background: #22202080;
  outline: none;
  font-size: 1rem;

  &:not(:placeholder-shown) {
    border: 1px solid ${(props) => props.theme.textFaded};
  }

  &:active,
  &:focus {
    border: 1px solid ${(props) => props.theme.colorOrange};
  }

  ${(props) =>
    props.invalid &&
    `
    border: 1px solid ${props.theme.colorRed};
  `}
`
export const InvalidText = styled.p`
  color: ${(props) => props.theme.colorRed};
  font-size: 0.75rem;
  margin-left: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  justify-content: flex-end;
`
